import type { GameId, RoleId } from '@ringmaster/shared';
import { whisperDistribution } from '@ringmaster/shared';
import { ROLE_PLAY_ORDER, roleCardDefFor } from './roleCardDefs.js';
import { RolePortrait } from './RolePortrait.js';

/**
 * RoleTally — the "who is at this table" strip: every role in a game's roster
 * with its portrait art and the ACTUAL dealt/pooled count for this table.
 * Zero-count roles stay visible but dimmed, so e.g. a 3-player Whisper table
 * still learns the Paladin exists. Originally The Whisper's DEAL-screen
 * component (still used unchanged there — see {@link WHISPER_TALLY_ROLES} +
 * the two whisper call sites); generalised to also drive Blood Moon: One
 * Night's pool panel (`hand/OneNightScreens.tsx`'s `OneNightPoolPanel`) so
 * every role display in either game gets the SAME portrait-avatar treatment
 * instead of a Glyph icon. `gameId` resolves the right character art via
 * {@link roleCardDefFor}; `accent` is the one visual knob a game gets (a flat
 * ring/badge tint — NOT per-role, so a game with a mixed-alignment roster
 * still reads as one coherent strip, exactly like the original Whisper look).
 *
 * The circular portrait itself is {@link RolePortrait} — shared with the
 * how-to-play role row and One Night's roles popover, so a role wears the same
 * face wherever it appears.
 */

export type RoleTallyAccent = 'arcana' | 'gold';

/**
 * The per-game strip tints. Exported because One Night's roles popover
 * (ui/OneNightRolesButton.tsx) lists the same five roles directly under this
 * strip — it takes the ring and label colours from here so the two halves of
 * that one panel can't drift into two different colour languages.
 */
export const ROLE_TALLY_ACCENT: Record<RoleTallyAccent, { ring: string; glow: string; badge: string; badgeBorder: string; label: string }> = {
  arcana: {
    ring: 'rgba(139,92,246,0.55)',
    glow: '0 0 18px rgba(139,92,246,0.25)',
    badge: 'var(--arcana-2, #5b3fa8)',
    badgeBorder: 'rgba(201,182,255,0.4)',
    label: 'var(--arcana-4, #c9b6ff)',
  },
  gold: {
    ring: 'rgba(230,184,102,0.55)',
    glow: '0 0 18px rgba(230,184,102,0.25)',
    badge: 'var(--gold-3, #a9762f)',
    badgeBorder: 'rgba(247,227,176,0.4)',
    label: 'var(--gold-5, #e6b866)',
  },
};

/**
 * The Whisper's own three roles, in the original strip order — now just this
 * game's row of the shared {@link ROLE_PLAY_ORDER} map, kept under its old name
 * for the two existing call sites.
 */
export const WHISPER_TALLY_ROLES: RoleId[] = ROLE_PLAY_ORDER.whisper;

export function RoleTally({
  gameId,
  roles,
  distribution,
  accent = 'arcana',
}: {
  gameId: GameId;
  roles: RoleId[];
  distribution: Partial<Record<RoleId, number>>;
  accent?: RoleTallyAccent;
}) {
  const tint = ROLE_TALLY_ACCENT[accent];

  return (
    <div style={{ display: 'flex', gap: 'clamp(14px, 3vw, 26px)', justifyContent: 'center', flexWrap: 'wrap' }}>
      {roles.map((roleId) => {
        const def = roleCardDefFor(gameId, roleId);
        const n = distribution[roleId] ?? 0;
        return (
          <div
            key={roleId}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              opacity: n === 0 ? 0.35 : 1,
            }}
          >
            <RolePortrait
              gameId={gameId}
              role={roleId}
              size={52}
              ring={`1px solid ${n > 0 ? tint.ring : 'var(--surface-stroke)'}`}
              glow={n > 0 ? tint.glow : 'none'}
            >
              <span
                style={{
                  position: 'absolute',
                  right: -6,
                  bottom: -4,
                  minWidth: 20,
                  height: 20,
                  padding: '0 5px',
                  borderRadius: 999,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#fff',
                  background: n > 0 ? tint.badge : 'rgba(60,60,60,0.9)',
                  border: `1px solid ${tint.badgeBorder}`,
                }}
              >
                ×{n}
              </span>
            </RolePortrait>
            <span
              style={{
                fontFamily: 'var(--font-ui)',
                fontSize: 12,
                fontWeight: 600,
                color: n > 0 ? tint.label : 'var(--text-tertiary)',
              }}
            >
              {def.name}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * WhisperTally — the original call-site shape (`playerCount` in, tally out),
 * now a thin wrapper over the generalised {@link RoleTally}. `whisperDistribution`
 * throws for an out-of-range roster (mid-join churn) — hide rather than lie,
 * exactly like the pre-generalisation component did.
 */
export function WhisperTally({ playerCount }: { playerCount: number }) {
  let dist: Partial<Record<RoleId, number>>;
  try {
    dist = whisperDistribution(playerCount);
  } catch {
    return null;
  }
  return <RoleTally gameId="whisper" roles={WHISPER_TALLY_ROLES} distribution={dist} accent="arcana" />;
}
